import { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { ChevronLeft } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { rolesAdminApi, type PermissionDto } from "@/lib/roles-admin";
import { Button } from "@/components/ui/button";

const KEY = ["admin", "roles"] as const;

export function RolesAdminPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Set<string> | null>(null);

  const catalogueQ = useQuery({ queryKey: ["admin", "permissions"], queryFn: rolesAdminApi.permissions });
  const rolesQ = useQuery({ queryKey: KEY, queryFn: rolesAdminApi.roles });

  const current = rolesQ.data?.find((r) => r.role === selected) ?? null;
  const checked = draft ?? new Set(current?.permissions ?? []);

  const grouped = useMemo(() => {
    const m = new Map<string, PermissionDto[]>();
    for (const p of catalogueQ.data ?? []) {
      const list = m.get(p.group) ?? [];
      list.push(p);
      m.set(p.group, list);
    }
    return [...m.entries()];
  }, [catalogueQ.data]);

  const save = useMutation({
    mutationFn: () => rolesAdminApi.setPermissions(selected!, [...checked]),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      setDraft(null);
    },
  });

  const reset = useMutation({
    mutationFn: () => rolesAdminApi.reset(selected!),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      setDraft(null);
    },
  });

  function toggle(key: string) {
    const next = new Set(checked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setDraft(next);
  }

  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(document.getElementById("topbar-left-portal"));
  }, []);

  return (
    <div className="space-y-4 p-4">
      {portalNode && createPortal(
        <Link to="/admin" className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back
        </Link>,
        portalNode
      )}
      <h1 className="text-lg font-semibold">Roles &amp; permissions</h1>
      <p className="text-sm text-muted-foreground">
        Choose a role, then tick the permissions it grants. Changes apply to a user on
        their next sign-in or token refresh (within ~15 minutes).
      </p>
      {rolesQ.isLoading && (
        <p className="text-sm text-muted-foreground">Loading roles…</p>
      )}
      <div className="flex flex-wrap gap-2">
        {(rolesQ.data ?? []).map((r) => (
          <Button
            key={r.role}
            variant={r.role === selected ? "default" : "outline"}
            size="sm"
            aria-pressed={r.role === selected}
            onClick={() => {
              setSelected(r.role);
              setDraft(null);
            }}
          >
            {r.role}
          </Button>
        ))}
      </div>
      {selected && (
        <>
          <div className="space-y-4">
            {grouped.map(([group, perms]) => (
              <fieldset key={group} className="rounded border border-border p-3">
                <legend className="px-1 text-sm font-medium">{group}</legend>
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {perms.map((p) => (
                    <label key={p.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={checked.has(p.key)}
                        onChange={() => toggle(p.key)}
                      />
                      {p.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              Save
            </Button>
            <Button
              variant="outline"
              onClick={() => reset.mutate()}
              disabled={reset.isPending}
            >
              Reset to defaults
            </Button>
          </div>
          {(save.isError || reset.isError) && (
            <p className="text-sm text-destructive">
              Couldn&apos;t save changes. Please try again.
            </p>
          )}
        </>
      )}
    </div>
  );
}
